import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { InvitationResponse, Mail, MailClass, MailInvitation, MailPage, MailThread, MailVerdict } from '../api/types.js';
import { useAskMailSession, useCreateDraft, useMail, useMailVerdict, useRemoveNoise, useRespondInvitation } from '../hooks/use-mail.js';
import { Button } from './button.js';
import { PlainText } from './plain-text.js';
import { timeAgo } from '../utils/time-ago.js';

// « Mail »: the operator's mailbox of this server's Google connection, as the API read and triaged it (ADR-036). What
// waits on him first (urgent, to answer), then what is for his information, then the invitations; noise and covered mail
// folded at the end, there to be checked and corrected. Everything a message says is text from others: plain text only.
// His verdicts (Important, Not important, Noise from this sender) are what the triage learns from.

const ORDER: MailClass[] = ['urgent', 'to-answer', 'invitation', 'to-read', 'covered', 'noise'];
const TITLE: Record<MailClass, string> = {
  urgent: 'Urgent', 'to-answer': 'To answer', invitation: 'Invitations', 'to-read': 'To read', covered: 'Covered elsewhere', noise: 'Noise',
};
const WHY: Record<MailClass, string> = {
  urgent: 'An incident, a deadline, an escalation: the triage put these first.',
  'to-answer': 'Someone asks you something or waits for you.',
  invitation: 'Calendar invitations that arrived by mail.',
  'to-read': 'For your information; no answer expected.',
  covered: 'Notifications of systems aidev follows itself (GitHub, Jira, the calendar).',
  noise: 'Newsletters, marketing, automatic mail. Not shown elsewhere.',
};
const TONE: Partial<Record<MailClass, string>> = { urgent: 'var(--color-error)', 'to-answer': 'var(--color-warn)' };
const FOLDED = new Set<MailClass>(['covered', 'noise']);
const VERDICT_LABEL: Record<MailVerdict, string> = { important: 'Important', 'not-important': 'Not important', 'noise-sender': 'Noise from this sender' };

/** The exact time, in the operator's own zone (the browser's) */
const when = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

function AuthLine({ m }: { m: Mail }) {
  const failed = (['spf', 'dkim', 'dmarc'] as const).filter((k) => m.auth?.[k] && m.auth[k] !== 'pass' && m.auth[k] !== 'none');
  if (!failed.length) return null;
  return <p className="text-xs" style={{ color: 'var(--color-warn)' }}>Authentication: {failed.map((k) => `${k} ${m.auth[k]}`).join(', ')}. Read it with care.</p>;
}

const ANSWER_BUTTON: Record<InvitationResponse, string> = { accepted: 'Accept', tentative: 'Tentative', declined: 'Decline' };
const ANSWERED: Record<string, string> = { accepted: 'You accepted', tentative: 'You answered tentative', declined: 'You declined' };
const SUGGESTED: Record<InvitationResponse | 'ask', string> = { accepted: 'accept', tentative: 'answer tentative', declined: 'decline', ask: 'ask before answering' };
const day = new Intl.DateTimeFormat('en-GB', { day: 'numeric' });
const weekdayMonth = new Intl.DateTimeFormat('en-GB', { weekday: 'short', month: 'short' });
const clock = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });

/** An all-day date (`2026-10-20`) is a day in his zone, not midnight UTC */
const eventDate = (v: string, allDay?: boolean) => (allDay ? new Date(`${v}T00:00:00`) : new Date(v));

/**
 * A calendar invitation in the thread (ADR-036 section 7): the event as his calendar holds it, the session's suggested
 * answer as the main button, his answer recorded on the event from here, the organiser notified. He can change it.
 */
function InvitationBlock({ serverId, threadId, inv, isAdmin }: { serverId: string; threadId: string; inv: MailInvitation; isAdmin: boolean }) {
  const respond = useRespondInvitation(serverId);
  const start = eventDate(inv.start, inv.allDay);
  const valid = !Number.isNaN(start.getTime());
  const time = inv.allDay ? 'All day' : valid ? `${clock.format(start)} to ${clock.format(eventDate(inv.end))}` : '';
  const answered = inv.response ?? (inv.status && inv.status !== 'needsAction' ? { value: inv.status as InvitationResponse } : undefined);
  const suggested = inv.recommendation?.value;
  return (
    <div className="mt-3 border-t pt-3" style={{ borderColor: 'var(--color-border)' }}>
      <div className="flex items-start gap-3">
        {valid && (
          <div className="w-14 shrink-0 rounded-md border text-center" style={{ borderColor: 'var(--color-border-2)', backgroundColor: 'var(--color-surface-2)' }} aria-hidden="true">
            <div className="pt-1 text-2xl font-semibold leading-none tabular-nums" style={{ color: inv.cancelled ? 'var(--color-text-3)' : 'var(--color-text)' }}>{day.format(start)}</div>
            <div className="pb-1 pt-0.5 text-[11px]" style={{ color: 'var(--color-text-3)' }}>{weekdayMonth.format(start)}</div>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium" style={{ color: 'var(--color-text)', textDecoration: inv.cancelled ? 'line-through' : undefined }}>{inv.summary}</p>
          <p className="text-xs tabular-nums" style={{ color: 'var(--color-text-2)' }}>
            <span className="sr-only">{valid ? when.format(start) : inv.start}, </span>{time}
          </p>
          {inv.organizer && <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>Organised by {inv.organizer}</p>}
        </div>
      </div>
      {inv.cancelled ? (
        <p className="mt-2 text-sm" style={{ color: 'var(--color-text-2)' }}>Cancelled by the organiser: nothing to answer.</p>
      ) : (
        <div className="mt-2 space-y-2">
          {inv.recommendation && (
            <p className="text-sm" style={{ color: 'var(--color-text)' }}>The session suggests to {SUGGESTED[inv.recommendation.value]}: {inv.recommendation.reason}</p>
          )}
          <p className="text-xs" style={{ color: 'var(--color-text-3)' }} aria-live="polite">
            {answered ? `${ANSWERED[answered.value] ?? 'You answered'}${inv.response ? ` on ${when.format(new Date(inv.response.at))}` : ' in Google Calendar'}. The organiser sees it.` : 'Not answered yet.'}
          </p>
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label={`Answer the invitation ${inv.summary}`}>
              {(['accepted', 'tentative', 'declined'] as InvitationResponse[]).map((r) => (
                <Button key={r} size="sm" variant={suggested === r ? 'primary' : 'ghost'} className="max-lg:min-h-10" aria-pressed={answered?.value === r}
                  disabled={respond.isPending || answered?.value === r} onClick={() => respond.mutate({ threadId, response: r })}>
                  {respond.isPending && respond.variables?.response === r ? 'Recording…' : ANSWER_BUTTON[r]}
                </Button>
              ))}
            </div>
          )}
          {respond.error && <p role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>Not recorded: {(respond.error as Error).message}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * The reply the thread's session drafted (ADR-036 section 3): the note for the operator, the reply to edit, Create the
 * draft; once created, where it is. Shown under the thread's last message.
 */
function ThreadReply({ serverId, t, isAdmin }: { serverId: string; t: MailThread; isAdmin: boolean }) {
  const create = useCreateDraft(serverId);
  const [body, setBody] = useState(t.reply?.reply ?? '');
  const field = 'block w-full rounded-md px-2.5 py-1.5 text-sm font-[inherit] leading-relaxed focus:outline-none focus-visible:outline focus-visible:outline-2';
  const fieldStyle = { backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border-2)', color: 'var(--color-text)' };
  const invitation = t.invitation ? <InvitationBlock serverId={serverId} threadId={t.threadId} inv={t.invitation} isAdmin={isAdmin} /> : null;
  if (t.status === 'queued') return <>{invitation}<p className="mt-2 text-xs" style={{ color: 'var(--color-text-3)' }}>The session is reading the thread; its {t.invitation ? 'suggestion' : 'reply'} shows here.</p></>;
  if (t.status === 'refused') {
    return (
      <>
        {invitation}
        <p role="alert" className="mt-2 text-xs" style={{ color: 'var(--color-error)' }}>
          The session&apos;s reply could not be used: {(t.errors ?? []).join('; ')}.
          {t.sessionId && <> <Link to={`/servers/${serverId}/sessions/${t.sessionId}`} className="underline" style={{ color: 'var(--color-brand-light)' }}>Open the session</Link></>}
        </p>
      </>
    );
  }
  if (!t.reply) return invitation;
  // An invitation without a reply to write: the answer on the event says enough
  const noReply = !!t.invitation && !t.reply.reply && !t.draft;
  return (
    <>
    {invitation}
    <div className="mt-3 space-y-2 border-t pt-3" style={{ borderColor: 'var(--color-border)' }}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-xs font-semibold" style={{ color: 'var(--color-text-2)' }}>The session&apos;s note</p>
        {/* The exchange goes on with the session that read the thread */}
        {t.sessionId && (
          <Link to={`/servers/${serverId}/sessions/${t.sessionId}`} className="text-xs hover:underline" style={{ color: 'var(--color-brand-light)' }}>Continue in the session</Link>
        )}
      </div>
      <PlainText text={t.reply.noteFr} className="text-sm" />
      {t.reply.waitOn && <p className="text-xs" style={{ color: 'var(--color-warn)' }}>Waits on you: {t.reply.waitOn}</p>}
      {noReply ? null : t.draft ? (
        <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>
          Draft created in Gmail{' '}
          <a href="https://mail.google.com/mail/u/0/#drafts" target="_blank" rel="noreferrer" className="font-medium underline" style={{ color: 'var(--color-brand-light)' }}>(open the drafts)</a>: read it and send it there.
        </p>
      ) : isAdmin ? (
        <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); create.mutate({ threadId: t.threadId, body: body.trim() }); }} aria-label={`Reply to ${t.subject}`}>
          <label className="block text-xs" style={{ color: 'var(--color-text-3)' }}>
            Your reply
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} maxLength={4000} className={`${field} mt-1`} style={fieldStyle} />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" size="sm" className="max-lg:min-h-10" disabled={!body.trim() || create.isPending}>{create.isPending ? 'Creating the draft…' : 'Create the draft in Gmail'}</Button>
            <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>A draft in the thread, under your account; nothing is sent.</span>
          </div>
          {create.error && <p role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>Not created: {(create.error as Error).message}</p>}
        </form>
      ) : (
        <PlainText text={t.reply.reply} className="text-sm" />
      )}
    </div>
    </>
  );
}

function MailCard({ serverId, m, isAdmin, open, thread, threadRecord }: { serverId: string; m: Mail; isAdmin: boolean; open: boolean; thread?: MailThread; threadRecord?: MailThread }) {
  const verdict = useMailVerdict(serverId);
  const ask = useAskMailSession(serverId);
  // The thread's session for any mail, in three cases: the thread has none yet; it has one that is not reading, asked
  // again from another mail of the thread; or its reply on this very mail was refused. Never while it reads (queued).
  const canAsk = isAdmin && (!threadRecord || (threadRecord.status !== 'queued' && (threadRecord.lastMessageId !== m.messageId || threadRecord.status === 'refused')));
  const [expanded, setExpanded] = useState(open);
  const from = m.from?.name ? `${m.from.name} <${m.from.email}>` : m.from?.email ?? '?';
  const detailsId = `mail-${m.messageId}`;
  return (
    <li id={m.key} className="rounded-md border p-3" style={{ borderColor: open ? 'var(--color-brand)' : 'var(--color-border)' }}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <button type="button" onClick={() => setExpanded((e) => !e)} aria-expanded={expanded} aria-controls={detailsId}
          className="min-w-0 flex-1 text-left text-sm font-medium hover:underline focus-visible:outline focus-visible:outline-2 max-lg:min-h-10" style={{ color: 'var(--color-text)' }}>
          {m.subject || '(no subject)'}
        </button>
        <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-3)' }} title={when.format(new Date(m.receivedAt))}>{timeAgo(m.receivedAt)}</span>
      </div>
      <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-2)' }}>{from}{m.attachments?.length ? ` · ${m.attachments.length === 1 ? '1 attachment' : `${m.attachments.length} attachments`}` : ''}</p>
      {m.triage && <p className="mt-1 text-xs" style={{ color: 'var(--color-text-3)' }}>{m.triage.reason}{m.triage.people?.length ? ` · ${m.triage.people.join(', ')}` : ''}</p>}
      <AuthLine m={m} />
      {expanded && (
        <div id={detailsId} className="mt-2 space-y-2">
          <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>To {(m.to ?? []).join(', ') || '?'}{m.cc?.length ? `, cc ${m.cc.join(', ')}` : ''}</p>
          <div className="max-h-96 overflow-auto rounded-md border p-2" style={{ borderColor: 'var(--color-border-2)', backgroundColor: 'var(--color-surface-2)' }}>
            <PlainText text={m.body || m.snippet || '(empty)'} className="text-sm" />
          </div>
          {(m.attachments?.length ?? 0) > 0 && (
            <ul className="text-xs" style={{ color: 'var(--color-text-2)' }}>
              {m.attachments.map((a, i) => <li key={i}>{a.name} ({a.type}, {Math.round(a.size / 1024)} KB), not read</li>)}
            </ul>
          )}
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {canAsk && (
          <Button size="sm" variant="ghost" className="max-lg:min-h-10" disabled={ask.isPending} onClick={() => ask.mutate({ key: m.key })}>
            {ask.isPending ? 'Starting the session…' : threadRecord ? 'Ask the session again' : 'Answer with a session'}
          </Button>
        )}
        {m.verdict ? (
          <span className="text-xs" style={{ color: 'var(--color-text-2)' }}>You said: {VERDICT_LABEL[m.verdict.value]}.</span>
        ) : isAdmin ? (
          (['important', 'not-important', 'noise-sender'] as MailVerdict[]).map((v) => (
            <Button key={v} size="sm" variant="ghost" className="max-lg:min-h-10" disabled={verdict.isPending} onClick={() => verdict.mutate({ key: m.key, verdict: v })}>{VERDICT_LABEL[v]}</Button>
          ))
        ) : null}
        <a href={`https://mail.google.com/mail/u/0/#all/${encodeURIComponent(m.threadId)}`} target="_blank" rel="noreferrer" className="text-xs hover:underline" style={{ color: 'var(--color-brand-light)' }}>Open in Gmail</a>
      </div>
      {verdict.error && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>Not recorded: {(verdict.error as Error).message}</p>}
      {ask.error && <p role="alert" className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>Session not started: {(ask.error as Error).message}</p>}
      {thread && <ThreadReply serverId={serverId} t={thread} isAdmin={isAdmin} />}
    </li>
  );
}

function ClassSection({ serverId, cls, mail, isAdmin, openKey, threads }: { serverId: string; cls: MailClass; mail: Mail[]; isAdmin: boolean; openKey: string | null; threads: Map<string, MailThread> }) {
  const folded = FOLDED.has(cls) && !mail.some((m) => m.key === openKey);
  const body = (
    <ul className="mt-2 space-y-2">
      {mail.map((m) => {
        const t = threads.get(m.threadId);
        // The reply goes under the message the session last read
        return <MailCard key={m.key} serverId={serverId} m={m} isAdmin={isAdmin} open={m.key === openKey} thread={t && t.lastMessageId === m.messageId ? t : undefined} threadRecord={t} />;
      })}
    </ul>
  );
  const heading = <h3 className="text-sm font-semibold" style={{ color: TONE[cls] ?? 'var(--color-text)' }}>{TITLE[cls]} ({mail.length})</h3>;
  return (
    <section className="mb-6" aria-label={TITLE[cls]}>
      {folded ? (
        <details>
          <summary className="cursor-pointer max-lg:min-h-10">{heading}</summary>
          <p className="mt-1 text-xs" style={{ color: 'var(--color-text-3)' }}>{WHY[cls]}</p>
          {body}
        </details>
      ) : (
        <>
          {heading}
          <p className="mt-1 text-xs" style={{ color: 'var(--color-text-3)' }}>{WHY[cls]}</p>
          {body}
        </>
      )}
    </section>
  );
}

/**
 * What the triage applies before any model (ADR-036 section 4): the senders he marked as noise here, which he can
 * remove, and the lists of the server's SDLC config, read-only. Folded: it is there to check, not to read every day.
 */
function TriageRules({ serverId, lists, isAdmin }: { serverId: string; lists: NonNullable<MailPage['lists']>; isAdmin: boolean }) {
  const remove = useRemoveNoise(serverId);
  const count = lists.learnedNoise.length + lists.noise.length + lists.priority.length;
  const muted = { color: 'var(--color-text-3)' };
  return (
    <details className="mb-4 text-xs" style={{ color: 'var(--color-text-2)' }}>
      <summary className="cursor-pointer max-lg:min-h-10">Rules before the triage ({count})</summary>
      <div className="mt-2 space-y-3 border-l pl-3" style={{ borderColor: 'var(--color-border)' }}>
        <div>
          <p className="font-medium" style={{ color: 'var(--color-text)' }}>Noise, marked by you</p>
          {lists.learnedNoise.length === 0 ? (
            <p style={muted}>None yet. Noise from this sender on a mail adds its sender here.</p>
          ) : (
            <ul className="mt-1 space-y-1">
              {lists.learnedNoise.map((s) => (
                <li key={s} className="flex flex-wrap items-center gap-2">
                  <span className="break-all">{s}</span>
                  {isAdmin && (
                    <Button size="sm" variant="ghost" className="max-lg:min-h-10" aria-label={`Remove ${s} from noise`}
                      disabled={remove.isPending && remove.variables?.sender === s} onClick={() => remove.mutate({ sender: s })}>Remove</Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {remove.error && <p role="alert" style={{ color: 'var(--color-error)' }}>Not removed: {(remove.error as Error).message}</p>}
        </div>
        <div>
          <p className="font-medium" style={{ color: 'var(--color-text)' }}>Set in the server&apos;s SDLC config</p>
          <p className="mt-1">Always noise: {lists.noise.length ? lists.noise.join(', ') : <span style={muted}>none</span>}</p>
          <p>Always to answer: {lists.priority.length ? lists.priority.join(', ') : <span style={muted}>none</span>}</p>
          <p style={muted}><code>gmail.noise</code> and <code>gmail.priority</code>: senders or whole domains.</p>
        </div>
      </div>
    </details>
  );
}

export function MailTab({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const { data, error, isLoading } = useMail(serverId);
  const [params] = useSearchParams();
  const threadParam = params.get('thread');
  const muted = { color: 'var(--color-text-2)' };
  if (isLoading) return <p className="text-sm" style={{ color: 'var(--color-text-3)' }}>Reading the mail…</p>;
  if (error) return <p role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>Could not read the mail: {(error as Error).message}</p>;
  if (!data?.enabled) return <p className="text-sm" style={muted}>Mail is not read for this server: set <code>gmail.triage</code> in its SDLC config.</p>;
  if (!data.connected) return <p className="text-sm" style={muted}>Mail is read from the server&apos;s Google connection: <Link to={`/servers/${serverId}/settings#notify-title`} className="underline" style={{ color: 'var(--color-brand-light)' }}>connect Google</Link> first.</p>;
  if (data.needsReconnect) return <p className="text-sm" style={muted}>Google was connected before mail was read: <Link to={`/servers/${serverId}/settings#notify-title`} className="underline" style={{ color: 'var(--color-brand-light)' }}>reconnect Google</Link> and tick the Gmail permissions.</p>;
  const threads = new Map((data.threads ?? []).map((t) => [t.threadId, t] as const));
  // An inbox item names a message (?mail=) or a thread (?thread=): that card opens
  const openKey = params.get('mail') ?? (threadParam ? data.mail.find((m) => m.threadId === threadParam && m.messageId === threads.get(threadParam)?.lastMessageId)?.key ?? null : null);
  const byClass = new Map<MailClass, Mail[]>();
  for (const m of data.mail) {
    const cls = m.triage?.class ?? 'to-read';
    byClass.set(cls, [...(byClass.get(cls) ?? []), m]);
  }
  return (
    <div>
      <p className="mb-4 text-xs" style={{ color: 'var(--color-text-3)' }}>
        {data.email}, the last 7 days{data.polledAt ? `, read ${timeAgo(data.polledAt)}` : ', not read yet: the first pass only marks where the mailbox is'}.
        {data.error ? <span style={{ color: 'var(--color-warn)' }}> Last pass: {data.error}.</span> : ''}
        {' '}Nothing is sent from here: aidev reads, you answer in Gmail.
      </p>
      {data.lists && <TriageRules serverId={serverId} lists={data.lists} isAdmin={isAdmin} />}
      {data.mail.length === 0 && <p className="text-sm" style={muted}>No mail read yet.</p>}
      {ORDER.filter((cls) => byClass.has(cls)).map((cls) => <ClassSection key={cls} serverId={serverId} cls={cls} mail={byClass.get(cls)!} isAdmin={isAdmin} openKey={openKey} threads={threads} />)}
    </div>
  );
}
